import { Button } from "reactstrap";
import { Bed, Hospital, ChartColumn, ChartLine, ClipboardList } from "lucide-react";
import { LogOut } from "lucide-react";
import { X } from "lucide-react";
import React, { memo, useCallback } from "react";
import Images from "../../Helper/ImageConstants";
import { Menu, MenuItem } from "react-pro-sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useLogout from "../../hooks/useLogout";
import { selectCanViewImpact } from "../PracticeShared/HealinkImpact/canViewImpact";
import {
  saveDemoPrivacyBlur,
  useDemoPrivacyBlur,
} from "../DemoPrivacyBlur";
import { saveDebugMode, useDebugMode } from "../../utils/debugMode";

const DoctorSidebar = ({ toggleSidebar, setToggleSidebar }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logout = useLogout();
  // useLogout hard-navigates to this role's own sign-in (clean URL, no
  // redirect query) — no extra navigation here or it races the hook's.
  const handleLogoutClick = logout;
  const demoPrivacyBlur = useDemoPrivacyBlur();

  const canToggleDebugMode = useSelector((state) =>
    Boolean(state?.user?.user?.featureFlags?.debugMode),
  );
  const debugMode = useDebugMode();
  // Per-practice feature gate for the Treatment Coordination dashboard.
  // Off by default; admins flip it on from the practice settings form.
  const tcDashboardEnabled = useSelector(
    (state) =>
      Boolean(state?.user?.user?.defaultPractice?.tcDashboardEnabled),
  );

  // Off until an admin has hand-checked this practice's numbers against its PMS,
  // and on per provider.
  const canViewImpact = useSelector(selectCanViewImpact);

  const handlePatientsClick = useCallback(() => {
    navigate("/doctor/patients");
  }, [navigate]);

  const handlePracticesClick = useCallback(() => {
    navigate("/doctor/practices");
  }, [navigate]);

  const handleOfficeDashboardClick = useCallback(() => {
    navigate("/doctor/dashboard");
  }, [navigate]);

  const handleTreatmentCoordinationClick = useCallback(() => {
    navigate("/doctor/treatment-coordination");
  }, [navigate]);

  const handleHealinkImpactClick = useCallback(() => {
    navigate("/doctor/healink-impact");
  }, [navigate]);

  return (
    <div
      className={`sidebar-content shadow transition position-absolute d-flex flex-column`}
      style={{
        width: toggleSidebar ? "260px" : "0px",
        overflow: "hidden",
      }}
    >
      <div className="d-flex py-3 justify-content-center flex-shrink-0">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-4">
          <img src={Images.LOGO_IMG} alt="Healink" width={180} />
        </div>
        <div>
          {window.innerWidth <= 576 && (
            <Button
              data-testid="sidebar-mobile-close"
              onClick={() => setToggleSidebar(!toggleSidebar)}
              className="border-0 cursor-pionter text-align-center bg-transparent p-3"
            >
              <X size={20} className="text-danger me-3" />
            </Button>
          )}
        </div>
      </div>
      <Menu
        className="Menu fw-bolder px-3 flex-grow-1"
        rootStyles={{ overflow: "auto", minHeight: 0 }}
      >
        <MenuItem
          icon={<ChartColumn size={20} />}
          className={`${
            pathname.includes("/doctor/dashboard") ||
            pathname.includes("/doctor/office-dashboard")
              ? "activeMenuItem"
              : "MenuItem fw-medium"
          }`}
          onClick={handleOfficeDashboardClick}
        >
          {"Dashboard"}
        </MenuItem>
        {canViewImpact && (
          <MenuItem
            icon={<ChartLine size={20} />}
            className={`${
              pathname.includes("/doctor/healink-impact")
                ? "activeMenuItem"
                : "MenuItem fw-medium"
            }`}
            onClick={handleHealinkImpactClick}
          >
            {"Healink impact"}
          </MenuItem>
        )}
        {tcDashboardEnabled && (
          <MenuItem
            icon={<ClipboardList size={20} />}
            className={`${
              pathname.includes("/doctor/treatment-coordination")
                ? "activeMenuItem"
                : "MenuItem fw-medium"
            }`}
            onClick={handleTreatmentCoordinationClick}
          >
            {"Treatment Coordination"}
          </MenuItem>
        )}
        <MenuItem
          icon={<Bed size={20} />}
          className={`${
            pathname.includes("/doctor/patients")
              ? "activeMenuItem"
              : "MenuItem fw-medium"
          }`}
          onClick={handlePatientsClick}
        >
          {"Patients"}
        </MenuItem>
        <MenuItem
          icon={<Hospital size={20} />}
          className={`${
            pathname.includes("/doctor/practices")
              ? "activeMenuItem"
              : "MenuItem fw-medium"
          }`}
          onClick={handlePracticesClick}
        >
          {"Switch Practices"}
        </MenuItem>
      </Menu>
      <div className="px-3 pb-3 pt-2 flex-shrink-0">
        <div className="d-flex align-items-center mb-3 w-100">
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 35, minWidth: 35, height: 35 }}
          >
            <div className="form-check form-switch doctor-sidebar-privacy-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="doctor-sidebar-demo-privacy-blur"
                checked={demoPrivacyBlur}
                onChange={(e) => saveDemoPrivacyBlur(e.target.checked)}
              />
            </div>
          </span>
          <label
            className="form-check-label small text-muted mb-0"
            htmlFor="doctor-sidebar-demo-privacy-blur"
          >
            Privacy blur
          </label>
        </div>
        {canToggleDebugMode && (
          <div className="d-flex align-items-center mb-3 w-100">
            <span
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 35, minWidth: 35, height: 35 }}
            >
              <div className="form-check form-switch doctor-sidebar-privacy-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="doctor-sidebar-debug-mode"
                  checked={debugMode}
                  onChange={(e) => saveDebugMode(e.target.checked)}
                />
              </div>
            </span>
            <label
              className="form-check-label small text-muted mb-0"
              htmlFor="doctor-sidebar-debug-mode"
            >
              Debug mode
            </label>
          </div>
        )}
        <button
          type="button"
          className="MenuItem ps-menu-button d-flex align-items-center fw-medium w-100 border-0 bg-transparent text-start p-0"
          onClick={handleLogoutClick}
        >
          <span
            className="d-inline-flex align-items-center justify-content-center"
            style={{ width: 35, minWidth: 35, height: 35 }}
          >
            <LogOut size={18} />
          </span>
          {"Log Out"}
        </button>
      </div>
    </div>
  );
};

export default memo(DoctorSidebar);
